/**
 * BIL-10 — the one place the user waits for JoFotara.
 *
 * Every send (invoice screen, bulk send, return create / retry, a payment on
 * an auto-send method) goes to the government inside the request and answers
 * with the verdict. While that is in flight this dialog covers the app: it
 * cannot be dismissed, nothing behind it can be clicked, and leaving the page
 * asks first. A bulk send shows how many went through out of how many as each
 * answer lands; at the end the user sees what was not accepted and why, with
 * a button that resends exactly those.
 *
 * Driven from anywhere through `jofotaraSend` / `sendToJofotara` (plain
 * functions, so a mutationFn can use them), rendered once in `main.tsx`.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { Badge, Button, Dialog, Spinner, type BadgeTone } from '@/components/ui';
import { ApiError, errorMessage } from '@/lib/api';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';

/** What the API answers for one send (`einvoiceResult`). */
export interface EinvoiceSendResult {
  outcome: 'ACCEPTED' | 'REJECTED' | 'UNCERTAIN';
  message: string | null;
}

/** FAILED = the request itself was refused (already accepted, network…). */
export type SendOutcome = EinvoiceSendResult['outcome'] | 'FAILED';

export interface SendItem {
  key: string;
  /** What the user recognises the document by — usually its number. */
  label: string;
}

interface SendRow extends SendItem {
  outcome: SendOutcome;
  message: string | null;
}

interface SendState {
  open: boolean;
  phase: 'sending' | 'done';
  title: string;
  total: number;
  current: string | null;
  rows: SendRow[];
  resend: ((items: SendItem[]) => void) | null;
}

const IDLE: SendState = {
  open: false,
  phase: 'sending',
  title: '',
  total: 0,
  current: null,
  rows: [],
  resend: null,
};

const useSendStore = create<SendState>(() => IDLE);

/** Low-level control, for flows that send as a side effect of something else. */
export const jofotaraSend = {
  /** Open the blocking dialog for `total` documents. */
  begin(title: string, total: number) {
    useSendStore.setState({ ...IDLE, open: true, title, total });
  },
  working(label: string) {
    useSendStore.setState({ current: label });
  },
  record(row: SendRow) {
    useSendStore.setState((s) => ({ rows: [...s.rows, row] }));
  },
  /**
   * Switch to the summary. With nothing recorded (no document was due to be
   * sent) the dialog simply closes.
   */
  finish(resend?: (items: SendItem[]) => void) {
    if (!useSendStore.getState().rows.length) {
      useSendStore.setState(IDLE);
      return;
    }
    useSendStore.setState({ phase: 'done', current: null, resend: resend ?? null });
  },
  close() {
    useSendStore.setState(IDLE);
  },
};

function lang() {
  return useAuthStore.getState().user?.language ?? 'AR';
}

/**
 * Record one send's answer (or the error that stopped it). An error is not
 * always a failure: when the answer never arrived (network, timeout) or
 * another request is sending the same document right now, the government's
 * verdict is UNKNOWN — the server never sends a document twice, so the
 * refreshed screen shows the truth; "already accepted" is an acceptance.
 */
export function recordSend(item: SendItem, result: EinvoiceSendResult | null | undefined, error?: unknown) {
  if (error !== undefined) {
    const code = error instanceof ApiError ? error.code : null;
    if (code === 'JOFOTARA_ALREADY_SUBMITTED') {
      jofotaraSend.record({ ...item, outcome: 'ACCEPTED', message: i18n.t('jofotara:sendDialog.alreadyAccepted') });
    } else if (code === 'NETWORK_ERROR' || code === 'TIMEOUT' || code === 'JOFOTARA_ALREADY_QUEUED') {
      jofotaraSend.record({
        ...item,
        outcome: 'UNCERTAIN',
        message: i18n.t(
          code === 'JOFOTARA_ALREADY_QUEUED' ? 'jofotara:sendDialog.inFlightElsewhere' : 'jofotara:sendDialog.answerLost',
        ),
      });
    } else {
      jofotaraSend.record({ ...item, outcome: 'FAILED', message: errorMessage(error, lang()) });
    }
  } else if (result) {
    jofotaraSend.record({ ...item, outcome: result.outcome, message: result.message });
  }
}

/**
 * Send documents one after the other — never in parallel, so they leave in
 * the order given (the bulk tab passes them in issue order) — showing the
 * progress, then the summary. "Resend" runs the same send for what failed.
 */
export async function sendToJofotara({
  title,
  items,
  send,
  onSettled,
}: {
  title: string;
  items: SendItem[];
  send: (item: SendItem) => Promise<EinvoiceSendResult | null | undefined>;
  /** After every run (including resends) — refresh the screens behind. */
  onSettled?: () => void;
}): Promise<void> {
  jofotaraSend.begin(title, items.length);
  for (const item of items) {
    jofotaraSend.working(item.label);
    try {
      recordSend(item, await send(item));
    } catch (error) {
      recordSend(item, null, error);
    }
  }
  onSettled?.();
  jofotaraSend.finish((failed) => void sendToJofotara({ title, items: failed, send, onSettled }));
}

/**
 * Money first, then the government: for payments on a method flagged for
 * JoFotara auto-send, where the server sends the invoice inside the payment
 * request. `pay` reports each invoice's verdict as its payment returns; a
 * payment that fails is rethrown for the caller's own error handling, after
 * the dialog has shown whatever was already reported. "Resend" goes through
 * `resend` (the plain invoice send) — never through the payment again.
 */
export async function payThenReport<T>({
  title,
  total,
  pay,
  resend,
  onSettled,
}: {
  title: string;
  total: number;
  pay: (report: (item: SendItem, result: EinvoiceSendResult | null | undefined) => void) => Promise<T>;
  resend: (item: SendItem) => Promise<EinvoiceSendResult | null | undefined>;
  onSettled?: () => void;
}): Promise<T> {
  jofotaraSend.begin(title, total);
  const again = (failed: SendItem[]) => void sendToJofotara({ title, items: failed, send: resend, onSettled });
  try {
    return await pay((item, result) => recordSend(item, result));
  } finally {
    onSettled?.();
    jofotaraSend.finish(again);
  }
}

const TONE: Record<SendOutcome, BadgeTone> = {
  ACCEPTED: 'emerald',
  REJECTED: 'red',
  UNCERTAIN: 'purple',
  FAILED: 'red',
};

export function JofotaraSendDialog() {
  const { t } = useTranslation('jofotara');
  const state = useSendStore();
  const sending = state.open && state.phase === 'sending';

  // Closing the tab mid-send would leave the user not knowing the outcome.
  useEffect(() => {
    if (!sending) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [sending]);

  const done = state.rows.length;
  const count = (o: SendOutcome) => state.rows.filter((r) => r.outcome === o).length;
  const accepted = count('ACCEPTED');
  const notAccepted = state.rows.filter((r) => r.outcome !== 'ACCEPTED');
  const percent = state.total ? Math.round((done / state.total) * 100) : 0;
  const label: Record<SendOutcome, string> = {
    ACCEPTED: t('sendDialog.accepted'),
    REJECTED: t('sendDialog.rejected'),
    UNCERTAIN: t('sendDialog.uncertain'),
    FAILED: t('sendDialog.failed'),
  };

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => !open && !sending && jofotaraSend.close()}
      title={state.title}
      size="md"
      preventClose={sending}
      hideCloseButton={sending}
      data-testid="jofotara-send-dialog"
      footer={
        sending ? undefined : (
          <>
            {state.resend && notAccepted.length > 0 && (
              <Button
                icon={RotateCcw}
                data-testid="jofotara-send-resend"
                onClick={() => state.resend?.(notAccepted.map(({ key, label: l }) => ({ key, label: l })))}
              >
                {t('sendDialog.resend', { count: notAccepted.length })}
              </Button>
            )}
            <Button variant="secondary" data-testid="jofotara-send-close" onClick={() => jofotaraSend.close()}>
              {t('sendDialog.close')}
            </Button>
          </>
        )
      }
    >
      {sending ? (
        <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="jofotara-send-progress">
          <Spinner size="lg" className="text-blue-600" />
          <p className="text-sm font-bold text-slate-800">{t('sendDialog.sending')}</p>
          <p className="text-xs text-slate-500">{t('sendDialog.wait')}</p>
          {state.total > 1 && (
            <div className="flex w-full flex-col gap-1.5">
              <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
                <div className="h-full bg-blue-600 transition-all" style={{ width: `${percent}%` }} />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
                <span className="font-mono font-bold" dir="ltr" data-testid="jofotara-send-count">
                  {t('sendDialog.progress', { done, total: state.total })}
                </span>
                <Badge tone="emerald">
                  {label.ACCEPTED}: {accepted}
                </Badge>
                {notAccepted.length > 0 && (
                  <Badge tone="red">
                    {t('sendDialog.notAccepted')}: {notAccepted.length}
                  </Badge>
                )}
              </div>
            </div>
          )}
          {state.current && (
            <p className="text-[11px] text-slate-500">
              {t('sendDialog.current')}{' '}
              <span className="font-mono font-bold text-slate-700" dir="ltr">
                {state.current}
              </span>
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3 text-xs" data-testid="jofotara-send-summary">
          {notAccepted.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-3 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              <p className="text-sm font-bold text-emerald-800">
                {t('sendDialog.allAccepted', { count: accepted })}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-slate-800" data-testid="jofotara-send-result-count">
                {t('sendDialog.summary', { accepted, total: done })}
              </span>
              {(['REJECTED', 'UNCERTAIN', 'FAILED'] as const).map((o) =>
                count(o) ? (
                  <Badge key={o} tone={TONE[o]}>
                    {label[o]}: {count(o)}
                  </Badge>
                ) : null,
              )}
            </div>
          )}
          {count('UNCERTAIN') > 0 && <p className="text-[11px] text-purple-800">{t('send.uncertainHint')}</p>}
          {state.rows.some((r) => r.outcome !== 'ACCEPTED' || r.message) && (
            <ul className="flex max-h-72 flex-col gap-1 overflow-auto" data-testid="jofotara-send-rows">
              {state.rows
                .filter((r) => r.outcome !== 'ACCEPTED' || r.message)
                .map((r) => (
                  <li
                    key={r.key}
                    className="flex items-start gap-2 border-b border-slate-100 pb-1"
                    data-row-key={r.key}
                    data-outcome={r.outcome}
                  >
                    <Badge tone={TONE[r.outcome]}>{label[r.outcome]}</Badge>
                    <span className="font-mono font-bold" dir="ltr">
                      {r.label}
                    </span>
                    <span className="whitespace-pre-wrap text-slate-700">{r.message}</span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}
